import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FiTrash2 } from 'react-icons/fi';
import XYZ from 'ol/source/XYZ';
import TileLayer from 'ol/layer/Tile';
import Check from 'components/Buttons/Check';
import { olMap, zoomToBounds } from 'components/Map/ControlMap';
import { clearError } from 'store/slices/offlineTilesSlice';
import { getTileToken } from 'store/services/offlineTilesService';
import Load from 'components/Load/Load';
import DeleteConfirmation from '../DeleteConfirmation';
import * as S from './styles';

export default function MapList() {
	const dispatch = useDispatch();

	const olMapInstance = olMap();

	const [selectedTiles, setSelectedTiles] = useState(new Set());
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [tileToDelete, setTileToDelete] = useState(null);
	const [deletingId, setDeletingId] = useState(null);
	const [errorActivate, setErrorActivate] = useState(null);

	const { list, loading, uploading, deleting, errorFetch, errorUpload, errorDelete } = useSelector(
		(state) => state.offlineTiles
	);

	const layersRef = useRef({});
	const prevUploadingRef = useRef(false);

	// Limpa erros temporários (upload/delete) automaticamente após 20s
	useEffect(() => {
		if (errorUpload || errorDelete) {
			const timer = setTimeout(() => dispatch(clearError()), 20000);
			return () => clearTimeout(timer);
		}
		return undefined;
	}, [errorUpload, errorDelete, dispatch]);

	// Limpa automaticamente a mensagem de erro de ativação após 20s
	useEffect(() => {
		if (errorActivate) {
			const timer = setTimeout(() => setErrorActivate(null), 20000);
			return () => clearTimeout(timer);
		}
		return undefined;
	}, [errorActivate]);

	// Após upload finalizar com sucesso, ativa automaticamente o primeiro mapa
	// Evita ativar se houve erro no upload
	useEffect(() => {
		if (prevUploadingRef.current && !uploading && list.length && !errorUpload) {
			activateMap(list[0]);
		}
		prevUploadingRef.current = uploading;
	}, [uploading, list]);

	// Cria layer de tiles autenticada via token (necessário para acesso protegido)
	const createTileLayer = async (tileId) => {
		const token = await getTileToken(tileId);

		return new TileLayer({
			source: new XYZ({
				url: `${process.env.REACT_APP_BASE_URL}/v1/tiles/${tileId}/{z}/{x}/{y}.webp?token=${token}`,
				crossOrigin: 'anonymous'
			})
		});
	};

	const formatFileSize = (bytes) => {
		if (bytes < 1024) return `${bytes} B`;
		if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
		return `${(bytes / 1048576).toFixed(2)} MB`;
	};

	// Ativa camada no mapa (ou reaproveita se já existir)
	// Mantém controle local das layers para evitar recriação desnecessária
	const activateMap = async (tile, { withZoom = true } = {}) => {
		try {
			const existingLayer = layersRef.current[tile.id];

			if (existingLayer) {
				existingLayer.setVisible(true);
			} else {
				const layer = await createTileLayer(tile.id);
				layer.set('tileId', tile.id);

				layersRef.current[tile.id] = layer;
				olMapInstance.addLayer(layer);
			}

			setSelectedTiles((prev) => {
				const next = new Set(prev);
				next.add(tile.id);
				return next;
			});

			if (withZoom) {
				zoomToBounds(tile.bounds, tile.max_zoom);
			}
		} catch (err) {
			setErrorActivate(err.message);
		}
	};

	// Alterna visibilidade do mapa no OpenLayers sem remover layer
	// Remove apenas da seleção, mantendo referência em memória
	const handleCheck = (tile) => {
		if (tile.status === 'error') return;

		if (selectedTiles.has(tile.id)) {
			setSelectedTiles((prev) => {
				const next = new Set(prev);
				next.delete(tile.id);

				const layer = layersRef.current[tile.id];
				if (layer) layer.setVisible(false);

				return next;
			});
		} else {
			activateMap(tile);
		}
	};

	// Abre modal de confirmação antes de excluir
	const handleDeleteClick = (tile) => {
		setTileToDelete(tile);
		setDeleteModalOpen(true);
	};

	// Remove layer do mapa e limpa referências locais
	// Executado somente após confirmação de exclusão bem-sucedida
	const handleRemoveFromMap = (id) => {
		const layer = layersRef.current[id];

		if (layer) {
			olMapInstance.removeLayer(layer);
			delete layersRef.current[id];
		}

		setSelectedTiles((prev) => {
			const next = new Set(prev);
			next.delete(id);
			return next;
		});
	};

	// Estado de loading inicial da lista
	if (loading) return <S.Loading>Carregando mapas...</S.Loading>;

	return (
		<>
			<S.ListContainer>
				{errorFetch && <S.ErrorMessage>Erro ao carregar lista de tiles: {errorFetch}</S.ErrorMessage>}
				{!list.length && !errorFetch && <S.Empty>Nenhum tile offline encontrado.</S.Empty>}
				{errorUpload && <S.ErrorMessage>Erro ao enviar: {errorUpload}</S.ErrorMessage>}
				{errorDelete && <S.ErrorMessage>Erro ao excluir: {errorDelete}</S.ErrorMessage>}
				{errorActivate && <S.ErrorMessage>Erro ao ativar: {errorActivate}</S.ErrorMessage>}
				{list.map((tile) => (
					<S.ListItem key={tile.id}>
						<Check
							isChecked={selectedTiles.has(tile.id)}
							inputType="checkbox"
							onClick={() => handleCheck(tile)}
							disabled={tile.status === 'error'}
						/>
						<S.MapInfo>
							<S.MapName>{tile.original_name}</S.MapName>
							{tile.status === 'error' ? (
								<S.ErrorText>
									{`${tile.error_message || 'Erro desconhecido.'}\nDeleta e tente enviar novamente.`}
								</S.ErrorText>
							) : (
								<S.MapDetails>{formatFileSize(tile.file_size)}</S.MapDetails>
							)}
						</S.MapInfo>
						<S.DeleteButton
							type="button"
							onClick={() => handleDeleteClick(tile)}
							disabled={deleting}
							title="Excluir mapa"
						>
							{deleting && deletingId === tile.id ? <Load /> : <FiTrash2 />}
						</S.DeleteButton>
					</S.ListItem>
				))}
			</S.ListContainer>

			<DeleteConfirmation
				isOpen={deleteModalOpen}
				onClose={() => {
					setDeleteModalOpen(false);
					setTileToDelete(null);
				}}
				mapName={tileToDelete?.original_name}
				mapId={tileToDelete?.id}
				setDeletingId={setDeletingId}
				onConfirmDelete={handleRemoveFromMap}
			/>
		</>
	);
}
